import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "../common/Avatar";
import FlagImg from "../agent-tender/FlagImg";
import PublicationNotice from "./PublicationNotice";
import PlayerAttachCard from "./v2/PlayerAttachCard";
import { MagnifierGlyph, CheckGlyph } from "./v2/cnGlyphs";
import { buildFinRows } from "../../utils/agentRequestFigures";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSIONS } from "../../config/permissions";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { clubNetworkService } from "../../services/clubNetworkApi";
import {
  formatRequestTitle,
  formatLeagueTier,
  formatTransferWindow,
} from "../../constants/agentTenderChoices";

// =====================================================================
// N2-3 — Message-agent modal, club side (Redesign 2.0 boards n2-3a /
// n2-3b / n2-3c + mobile board M3 @390px). Presentation layer only: the
// compose/attach/send state machine and every handler (#50-#57) are
// byte-identical to the pre-rebuild modal. The club writes a first message
// and may optionally attach ONE of its own players; the attach travels
// over the EXISTING chat attachment path (D-004).
//
// Artboard grammar:
//  N2-3a  520px dialog r14 (scrim rgba(17,24,39,0.45)): head "Message
//         {agent}" 16px/700 + context line 12px (title · tier + image
//         flag · fin figures · window) · message box #F3F4F6 r10 · dashed
//         attach affordance (26px "+" disc, "optional" right) · footer
//         Cancel / Send message (r8, 13px).
//  N2-3b  picker replaces the affordance: #BFDBFE border, blue shadow,
//         #F8FAFC search header with the verbatim S3 magnifier 13×13 +
//         "Search your squad…", rows 32px avatar / name 12.5px / meta
//         "Position · Age" / "Attach" 12px blue. Footer stays available
//         (SPEC-EXTRACT §7.19 — send/cancel never disappear).
//  N2-3c  attached card = shared PlayerAttachCard "attached" variant
//         (36px avatar, "· attached to this message", U+00D7 disc) ·
//         sent state = 44px #ECFDF5 disc + S4 check 18×14 green, "Sent"
//         14.5px/700, copy, secondary "Go to chat" — FUNCTION-TODO §B.1
//         (#56) flagged this as "fix = follow-up" (deferred during the
//         rebuild, never decided as permanent). QAV5-407: now deep-links
//         to the channel the send response already returned, same
//         /inbox?channel=<id> pattern as AgentSearchInbox.
//  M3     bottom sheet r18 18 0 0 (scrim 0.5): grab handle 36×4, head
//         15px/700 + context WITHOUT the window, message box 13.5px,
//         attached card "sheet" variant (38px avatar, "· attached", 24px
//         disc, no hover), full-width Send (r12/15px), centered footnote
//         "The conversation continues in your chats." M3 has no Cancel
//         button — the grab handle and the scrim tap call the SAME
//         onClose handler (#57). Desktop/sheet switch follows the
//         AgentTenderPreviewModal precedent (useMediaQuery ≤767px).
//
// ANONYMITY (locked): the modal renders the agent + request spec only —
// nothing club-resolving exists in the payload or is added here.
// =====================================================================

const MessageAgentModal = ({ request, onClose, onSent }) => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [attached, setAttached] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [squad, setSquad] = useState([]);
  const [squadQuery, setSquadQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null); // { channel_id } once sent
  // Send-side mirror of the contact_agents gate (the entry buttons are already
  // hidden without it; the backend refuses the contact regardless).
  const canContactAgents = usePermission(PERMISSIONS.CONTACT_AGENTS);
  // M3 is a SHEET presentation of the same modal (jsdom-safe: defaults false).
  const isSheet = useMediaQuery("(max-width: 767px)");

  // Load the squad once the picker is first opened (lazy — many clubs never
  // attach a player).
  useEffect(() => {
    let cancelled = false;
    if (pickerOpen && squad.length === 0) {
      clubNetworkService.getSquad().then((res) => {
        if (!cancelled && res.success) setSquad(res.data || []);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, squad.length]);

  const filteredSquad = useMemo(() => {
    const q = squadQuery.trim().toLowerCase();
    if (!q) return squad;
    return squad.filter((p) => p.name.toLowerCase().includes(q));
  }, [squad, squadQuery]);

  if (!request) return null;

  const agentName = request.responsible_agent?.display_name || "the agent";
  const windowInfo = formatTransferWindow(request.transfer_window);
  const windowLabel = windowInfo ? windowInfo.label || windowInfo.dates : null;
  const finRows = buildFinRows(request, "mo");
  const tierLabel = formatLeagueTier(request.league_tier);

  const handleSend = async () => {
    if (!canContactAgents) return;
    setSending(true);
    setError(null);
    const res = await clubNetworkService.messageAgent(request.id, {
      text,
      player: attached,
    });
    setSending(false);
    if (res.success) {
      setSent(res.data || {});
      if (onSent) onSent(request, res.data);
    } else {
      setError(res.error || "Could not send the message. Please try again.");
    }
  };

  // ── N2-3c B — sent confirmation (also the sheet's final state) ──────
  const sentState = (
    <div className="flex flex-col items-center text-center gap-[9px] py-2">
      <span className="w-11 h-11 rounded-full bg-[#ECFDF5] flex items-center justify-center">
        <CheckGlyph width={18} height={14} stroke="#16A34A" />
      </span>
      <span className="text-[14.5px] font-bold text-[#111827]">Sent</span>
      <span className="text-[12.5px] text-[#6B7280] leading-[1.55]">
        The conversation with {agentName} continues in your chats.
      </span>
      {/* QAV5-407 fix (was #56 — see file-header note): the send response
          already carries the channel, so deep-link straight to it. */}
      <button
        onClick={() => {
          onClose();
          navigate(`/inbox?channel=${sent.channel_id}`);
        }}
        className="mt-0.5 px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-lg text-[12.5px] font-semibold text-[#111827] hover:bg-[#F3F4F6]"
      >
        Go to chat
      </button>
    </div>
  );

  // ── Compose state — shared by dialog and sheet ──────────────────────
  const composeState = (
    <>
      <div className="flex flex-col gap-[3px]">
        <span className={`${isSheet ? "text-[15px]" : "text-[16px]"} font-bold text-[#111827]`}>
          Message {agentName}
        </span>
        {/* Context line — title · tier + image flag (Rule 3) · real fin
            figures · window (desktop only, M3 omits it). */}
        <span
          className={`${
            isSheet ? "text-[11.5px]" : "text-[12px]"
          } text-[#6B7280] flex items-center flex-wrap gap-x-1.5 gap-y-0.5`}
        >
          {/* T029-3: `capitalize` used to sit on the WHOLE context line, so it
              title-cased every word of the shared salary string and the modal
              read "Salary €20k - 500k Net / Mo" while the card — the
              in-product reference — reads "net / mo" (both come from the same
              formatSalaryLine). Scope it to the title only, exactly like the
              sibling surfaces do (AgentRequestListRow.js, and the detail page's
              h1), so the figures keep their canonical casing. */}
          <span className="capitalize">{formatRequestTitle(request)}</span>
          {tierLabel && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1">
                {tierLabel}
                <FlagImg
                  country={request.country}
                  className="w-4 h-[11px] rounded-[2px]"
                />
              </span>
            </>
          )}
          {finRows.map((r) => (
            <React.Fragment key={r.key}>
              <span aria-hidden="true">·</span>
              <span>
                {r.label} {r.value}
              </span>
            </React.Fragment>
          ))}
          {windowLabel && !isSheet && (
            <>
              <span aria-hidden="true">·</span>
              <span>{windowLabel}</span>
            </>
          )}
        </span>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Introduce your player or ask about the search…"
        className={`w-full bg-[#F3F4F6] border border-[#E5E7EB] rounded-[10px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          isSheet
            ? "min-h-[64px] pl-[13px] pr-[13px] py-[11px] text-[13.5px] leading-[1.5]"
            : "min-h-[76px] pl-[13px] pr-[13px] py-3 text-[13px] leading-[1.55]"
        }`}
      />

      {attached ? (
        // N2-3c A / M3 — the attached player travels with this message,
        // removable before send (handler #54).
        <PlayerAttachCard
          variant={isSheet ? "sheet" : "attached"}
          name={attached.name}
          avatar={attached.avatar || null}
          meta={
            <>
              {attached.position} · {attached.age} ·{" "}
              <span>{isSheet ? "attached" : "attached to this message"}</span>
            </>
          }
          onRemove={() => setAttached(null)}
          removeLabel="Remove attachment"
        />
      ) : pickerOpen ? (
        // N2-3b — compact picker of the club's own squad (lazy-loaded #51,
        // client-filtered #55, attach #53).
        <div className="border border-[#BFDBFE] rounded-[10px] overflow-hidden shadow-[0_4px_14px_rgba(34,131,255,0.12)]">
          <div className="flex items-center gap-2 bg-[#F8FAFC] border-b border-[#E5E7EB] pl-[13px] pr-[13px] py-[9px]">
            <MagnifierGlyph size={13} className="flex-none" />
            <input
              value={squadQuery}
              onChange={(e) => setSquadQuery(e.target.value)}
              placeholder="Search your squad…"
              className="flex-1 min-w-0 bg-transparent text-[12.5px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filteredSquad.length === 0 ? (
              <div className="pl-[13px] pr-[13px] py-3 text-[12px] text-[#9CA3AF]">
                No players match.
              </div>
            ) : (
              filteredSquad.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setAttached(p);
                    setPickerOpen(false);
                    setSquadQuery("");
                  }}
                  className="w-full flex items-center gap-2.5 pl-[13px] pr-[13px] py-2.5 hover:bg-[#F3F4F6] text-left"
                >
                  <Avatar src={p.avatar || null} fallbackText={p.name} size={32} />
                  <span className="flex flex-col flex-1 min-w-0">
                    <span className="text-[12.5px] font-semibold text-[#111827] truncate">
                      {p.name}
                    </span>
                    <span className="text-[11px] text-[#6B7280] truncate">
                      {p.position} · {p.age}
                    </span>
                  </span>
                  <span className="flex-none text-[12px] font-semibold text-[#2283FF]">
                    Attach
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        // N2-3a — dashed attach affordance (opens the picker, #52).
        <button
          onClick={() => setPickerOpen(true)}
          className="w-full flex items-center gap-[9px] border border-dashed border-[#D1D5DB] rounded-[10px] pl-3.5 pr-3.5 py-[11px] hover:border-[#2283FF] hover:bg-[#F8FAFC]"
        >
          <span className="w-[26px] h-[26px] rounded-full bg-[#E8F1FF] text-[#2283FF] inline-flex items-center justify-center text-[14px] font-semibold flex-none">
            +
          </span>
          <span className="text-[12.5px] font-semibold text-[#2283FF]">
            Attach one of your players
          </span>
          <span className="ml-auto text-[11.5px] text-[#9CA3AF]">optional</span>
        </button>
      )}

      {/* Publication clarity (DoD 23) — existing element, no artboard
          placement, kept wired best-fit (OPEN). */}
      <PublicationNotice variant="n2-message" />

      {!canContactAgents && (
        <p className="text-[12px] text-[#6B7280]">
          You don't have permission to contact agents. Ask your club admin.
        </p>
      )}
      {error && <p className="text-[12px] text-red-500">{error}</p>}

      {isSheet ? (
        // M3 — full-width primary in thumb reach + centered footnote.
        <>
          <button
            onClick={handleSend}
            disabled={sending || !text.trim() || !canContactAgents}
            className="w-full bg-[#2283FF] text-white rounded-xl text-[15px] font-semibold py-[15px] hover:bg-[#1B6FE0] disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
          <span className="text-[11px] text-[#9CA3AF] text-center">
            The conversation continues in your chats.
          </span>
        </>
      ) : (
        <div className="flex justify-end gap-2 border-t border-[#F3F4F6] pt-3.5">
          <button
            onClick={onClose}
            className="bg-white text-[#111827] border border-[#E5E7EB] rounded-lg text-[13px] font-semibold px-4 py-2.5 hover:bg-[#F3F4F6]"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !text.trim() || !canContactAgents}
            className="bg-[#2283FF] text-white rounded-lg text-[13px] font-semibold pl-[18px] pr-[18px] py-2.5 hover:bg-[#1B6FE0] disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </div>
      )}
    </>
  );

  if (isSheet) {
    // M3 — bottom sheet on the 0.5 scrim; scrim tap and grab handle both
    // run the existing close handler (#57 — M3 has no Cancel button).
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col justify-end bg-[rgba(17,24,39,0.5)]"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white rounded-t-[18px] pt-2.5 px-4 pb-4 flex flex-col gap-[13px] max-h-[85vh] overflow-y-auto">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="sheet-grab-handle"
            className="w-9 h-1 rounded-full bg-[#E5E7EB] self-center flex-none"
          />
          {sent ? sentState : composeState}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(17,24,39,0.45)] p-4">
      <div className="w-full max-w-[520px] bg-white rounded-[14px] shadow-[0_20px_50px_rgba(17,24,39,0.3)] pt-6 pb-6 pl-[26px] pr-[26px] flex flex-col gap-[14px] max-h-[85vh] overflow-y-auto">
        {sent ? sentState : composeState}
      </div>
    </div>
  );
};

export default MessageAgentModal;
